import { useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, api } from '../api';
import { useAuth, useTenantSession } from '../auth';
import { Banner, Field } from '../components/ui';

export function Account() {
  const session = useTenantSession();
  const { refresh } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (newPassword !== confirmPassword) {
      setError('The new passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await api.changePassword(session.token, currentPassword, newPassword);
      setNotice('Password changed.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change your password');
    } finally {
      setSubmitting(false);
    }
  }

  async function onResendVerification() {
    setError(null);
    setNotice(null);
    try {
      const res = await api.resendVerification(session.token);
      setNotice(res.sent ? 'Verification email sent.' : 'Your email is already verified.');
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send the verification email');
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Account</h1>
          <p className="muted">Your profile in {session.application?.name ?? 'this application'}.</p>
        </div>
      </div>

      {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}
      {notice && <Banner kind="success" onDismiss={() => setNotice(null)}>{notice}</Banner>}

      <section className="card">
        <h2 className="card__title">Profile</h2>
        <dl className="detail-list">
          <div>
            <dt>Name</dt>
            <dd>{session.user.name}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>
              {session.user.email}
              {session.user.emailVerified ? (
                <span className="pill pill--ok">Verified</span>
              ) : (
                <span className="pill pill--warn">Unverified</span>
              )}
            </dd>
          </div>
          <div>
            <dt>Role in application</dt>
            <dd>{session.user.isMaster ? 'Master user' : 'Member'}</dd>
          </div>
          <div>
            <dt>Application ID</dt>
            <dd>
              <code className="small">{session.user.tenantId}</code>
            </dd>
          </div>
        </dl>
        {!session.user.emailVerified && (
          <button type="button" className="btn btn--small" onClick={() => void onResendVerification()}>
            Resend verification email
          </button>
        )}
      </section>

      <section className="card">
        <h2 className="card__title">Effective permissions</h2>
        {session.permissions.includes('*') ? (
          <p className="muted">
            As a master user you hold every permission in this application, including ones added later.
          </p>
        ) : session.permissions.length === 0 ? (
          <p className="muted">You have no permissions yet. Ask a master user to assign you a role.</p>
        ) : (
          <span className="tag-list">
            {session.permissions.map((code) => (
              <span className="chip chip--code" key={code}>
                {code}
              </span>
            ))}
          </span>
        )}
      </section>

      <section className="card">
        <h2 className="card__title">Change password</h2>
        <form className="stack stack--narrow" onSubmit={onChangePassword}>
          <Field label="Current password">
            <input
              className="input"
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <Field
            label="New password"
            hint="At least 8 characters with an uppercase letter, a lowercase letter and a digit."
          >
            <input
              className="input"
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Confirm new password">
            <input
              className="input"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <div>
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
